import { i18n } from '@/translate/i18n'
import Swal, { SweetAlertIcon } from 'sweetalert2'

import { PAGE_PATHS } from './paths'
import { SESSION_KEYS, getParsed } from './browserStorage'
import { exitApp } from './functions'

interface Alert {
  icon: SweetAlertIcon
  title: string
  html: string
}

export const setCachedAlert = (alert: Alert) => {
  sessionStorage.setItem(SESSION_KEYS.client_alert, JSON.stringify(alert))
}

export const fireCachedAlert = () => {
  const alert = getParsed<Alert>(sessionStorage, SESSION_KEYS.client_alert)
  // console.log("TESTE fireCachedAlert alert", alert)
  if (!alert) return

  Swal.fire({
    icon: alert.icon,
    title: i18n.t(alert.title),
    html: i18n.t(alert.html),
    confirmButtonAriaLabel: 'btn_confirm',
    didOpen: () => {
      Swal.getConfirmButton().setAttribute('id', 'btn_success_modal_ok')
    }
  })
  sessionStorage.removeItem(SESSION_KEYS.client_alert)
}

export const onTokenSent = (email: string) => {
  Swal.fire({
    icon: 'success',
    title: i18n.t('swal.success'),
    html: i18n.t('messages.tokenSent', { email: email }),
    confirmButtonAriaLabel: 'btn_confirm'
  })
}

export const onPasswordReset = (
  autoLoginHandler: (email: string, password: string, isAuto: boolean) => void,
  email: string,
  password: string
) => {
  let message = i18n.t('messages.passwordChanged')
  if (autoLoginHandler) {
    message += ' ' + i18n.t('messages.autoLogin')
  }

  Swal.fire({
    icon: 'success',
    title: i18n.t('swal.success'),
    html: message,
    confirmButtonAriaLabel: 'btn_confirm',
    didOpen: () => {
      Swal.getConfirmButton().setAttribute('id', 'btn_success_modal_ok')
    },
    didClose: () => {
      if (autoLoginHandler) {
        autoLoginHandler(email, password, true)
      }
    }
  })
}

export const onAccountFinalized = (autoLoginHandler: () => void) => {
  Swal.fire({
    icon: 'success',
    title: i18n.t('swal.success'),
    html: i18n.t('messages.accountReady') + ' ' + i18n.t('messages.autoLogin'),
    confirmButtonAriaLabel: 'btn_confirm',
    didClose: autoLoginHandler
  })
}

export const onInvalidFinalizeToken = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.invalidLink')
  })
}

export const onFinalizeLinkAlreadyUsed = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.finalizeLinkAlreadyUsed')
  })
}

export const onAlreadySignedUp = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.alreadySignedUp'),
    didClose: exitApp
  })
}

export const onUncaughtError = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.tryAgain')
  })
}

export const codError = (message: string, callback?: () => void) => {
  Swal.fire({
    icon: 'error',
    title: i18n.t('swal.error'),
    html:
      i18n.t('messages.contactAdmin') + '<br>' + message + '<br>' + new Date(),
    didClose: () => callback && callback()
  })
}

export const loginAgain = (message?: string) => {
  Swal.fire({
    icon: 'error',
    title: i18n.t('swal.error'),
    html: i18n.t('messages.accessDenied') + '<br>' + message,
    didClose: exitApp
  })
}

export const contactAdmin = (callback?: () => void) => {
  Swal.fire({
    icon: 'error',
    title: i18n.t('swal.error'),
    html: i18n.t('messages.contactAdmin'),
    didClose: () => callback && callback()
  })
}

export const invalidEmail = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.invalidEmail')
  })
}

export const invalidPassword = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.wrongPassword')
  })
}

export const passwordTooShort = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.passwordTooShort')
  })
}

export const passwordsDontMatch = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.passwordsDontMatch')
  })
}

export const phoneTooShort = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.phoneTooShort')
  })
}

export const onPhoneDuplicatedError = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.phoneExists')
  })
}

export const idTooShort = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.idTooShort')
  })
}

export const guardianAgreementRequired = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.guardianData')
  })
}

export const legalAgreementRequired = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.agreement')
  })
}

export const accessDenied = message => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html:
      i18n.t('messages.accessDenied') + '<br>' + message + '<br>' + new Date(),
    didClose: exitApp
  })
}

export const serverUnreachable = () => {
  Swal.fire({
    icon: 'error',
    title: i18n.t('swal.error'),
    html: i18n.t('messages.unreachable'),
    didClose: exitApp
  })
}

export const contentNotAvailable = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.unavailable'),
    allowOutsideClick: false,
    allowEscapeKey: false,
    confirmButtonAriaLabel: 'btn_confirm',
    // @ts-expect-error type fix
    didClose: () => (window.location = PAGE_PATHS.home)
  })
}

export const contentDoesntExist = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.unexisting'),
    allowOutsideClick: false,
    allowEscapeKey: false,
    confirmButtonAriaLabel: 'btn_confirm',
    // @ts-expect-error type fix
    didClose: () => (window.location = PAGE_PATHS.home)
  })
}

export const dataUpdated = () => {
  Swal.fire({
    icon: 'success',
    title: i18n.t('swal.success'),
    html: i18n.t('messages.dataUpdated')
  })
}

export const onUserError = () => {
  Swal.fire({
    imageUrl: '/assets/images/userErrorLogin.png',
    imageWidth: 114,
    imageHeight: 114,
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.loginInvalid'),
    width: '380px',
    confirmButtonText: i18n.t('buttons.understood'),
    customClass: {
      popup: 'custom-error-popup'
    },
    didRender: () => {
      const popup = document.querySelector<HTMLElement>(
        '.swal2-popup.custom-error-popup'
      )
      if (popup) {
        popup.style.height = '394px'
        popup.style.borderRadius = '20px'
      }

      const title = document.querySelector<HTMLElement>('.swal2-title')
      if (title) {
        title.style.fontFamily = 'Inter'
        title.style.fontWeight = '600'
        title.style.fontSize = '20px'
        title.style.lineHeight = '100%'
        title.style.color = 'var(--grey2)'
      }

      const html = document.querySelector<HTMLElement>('.swal2-html-container')
      if (html) {
        html.style.fontFamily = 'Inter'
        html.style.fontWeight = '400'
        html.style.fontSize = '16px'
        html.style.lineHeight = '100%'
        html.style.color = 'var(--grey16)'
      }
    }
  })
}

export const onExpiredCode = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.tokenInvalid')
  })
}

export const onTooManyRequests = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.tooManyRequests')
  })
}

export const onTooManyFailedAttempts = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.tooManyFailedAttempts')
  })
}

export const onUserNotFoundInstitutional = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('messages.userNotFound'),
    html: i18n.t('messages.userNotFoundInstitutional'),
    confirmButtonAriaLabel: 'btn_confirm'
  })
}

export const onUserNotFound = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.userNotFound')
  })
}

export const onLimitExceeded = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.tooManyAttempts')
  })
}

export const onInvalidParameter = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.checkData')
  })
}

export const onMultipleFilesException = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.multipleImages')
  })
}

export const onFileTypeException = () => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.imageType', { support: '.jpg, .png, .webp' })
  })
}

export const onFileSizeException = (
  fileSize: number,
  limit: string = '10 MB'
) => {
  Swal.fire({
    icon: 'warning',
    title: i18n.t('swal.warning'),
    html: i18n.t('messages.imageSize', { size: fileSize, limit: limit })
  })
}
